import type { SubscriptionAddOnStatus } from '@scio3/shared-types';
import {
  OPTIDEV_BASE_CREDITS,
  OPTIDEV_CREDITS_PER_EXTRA_SCREEN,
  OPTIDEV_MIN_LICENSES,
} from './billing-constants';

export const OPTIDEV_ADDON_KEY = 'optidev';

/**
 * What the OptiDev billing surfaces are being asked to say.
 *
 * - `subscribe`  — on trial, or the trial is over and nothing is bought. The
 *                  customer is being asked to start paying.
 * - `info`       — paid, but the account has no room to add licenses (<= the
 *                  3 already included). Nothing on the screen is adjustable.
 * - `update`     — paid, with licenses that can be added or removed.
 */
export type OptiDevAddonMode = 'subscribe' | 'info' | 'update';

/**
 * A cancelled OR naturally expired trial leaves the entry in place with
 * `isActive:false, isTrial:false, maxQuantity:0` and `id:null` — see
 * `cancelTrialAddOn` / the trial-expiry cron in optisigns-open-api. It is NOT
 * removed, which is what separates "had it once" from "never had it" (the
 * latter has no entry at all).
 *
 * CAVEAT: a cancelled *paid* subscription lands in exactly the same shape, so
 * this predicate cannot tell the two apart. Copy driven by it must stay true
 * for both — see `optidevTrialEndLabelKind`.
 */
export function isOptidevInactive(sub: SubscriptionAddOnStatus | undefined): boolean {
  if (!sub) return false;
  return !sub.isActive && !sub.isTrial && (sub.maxQuantity ?? 0) === 0;
}

export function isOptidevOnTrial(sub: SubscriptionAddOnStatus | undefined): boolean {
  return !!sub?.isTrial && !!sub?.isActive;
}

export function resolveOptidevMode(
  sub: SubscriptionAddOnStatus | undefined,
  maxScreens: number,
): OptiDevAddonMode {
  if (isOptidevOnTrial(sub) || isOptidevInactive(sub)) return 'subscribe';
  return maxAddableLicenses(maxScreens) === 0 ? 'info' : 'update';
}

/**
 * Capped at the account's OptiSigns screen count because an OptiDev license
 * attaches to a screen — an account with 3 or fewer screens can add none.
 */
export function maxAddableLicenses(maxScreens: number): number {
  return Math.max((maxScreens ?? 0) - OPTIDEV_MIN_LICENSES, 0);
}

/**
 * Floor for the "Added" stepper. The backend rejects a quantity below the
 * number of screens actually using the add-on (`maxQuantity < activeDeviceCount`
 * in billing.service.ts), so the control must not offer a value it would refuse.
 */
export function minAddableLicenses(usedLicenses: number): number {
  return Math.max((usedLicenses ?? 0) - OPTIDEV_MIN_LICENSES, 0);
}

/**
 * Added licenses implied by a stored quantity. Clamped at 0 because a second
 * write path — the Screens "Manage licenses" matrix — persists OptiDev
 * `maxQuantity` with no 3-screen floor, so quantities of 1 or 2 exist in the
 * wild and would otherwise render a negative count.
 */
export function addedFromQuantity(quantity: number): number {
  return Math.max((quantity ?? 0) - OPTIDEV_MIN_LICENSES, 0);
}

export function quantityFromAdded(added: number): number {
  return OPTIDEV_MIN_LICENSES + Math.max(added ?? 0, 0);
}

/** Monthly on EVERY billing interval — an annual subscriber still gets this per month. */
export function optidevCreditsFor(quantity: number): number {
  return OPTIDEV_BASE_CREDITS + addedFromQuantity(quantity) * OPTIDEV_CREDITS_PER_EXTRA_SCREEN;
}

/**
 * Base-package price. Prefer the real Stripe tier-1 flat amount, which is the
 * only correct source when it differs from 3 × the per-screen rate (GBP is
 * £25 base against £8.35/screen). Falls back to the derivation the page has
 * always used when Stripe's per-currency tiers aren't loaded (USD skips that
 * query entirely).
 */
export function optidevBasePrice(basePrice: number | undefined, perScreen: number): number {
  return basePrice ?? perScreen * OPTIDEV_MIN_LICENSES;
}

/**
 * Which trial-ended copy is honest for this entry.
 *
 * `trialEndDate` survives cancellation untouched, so it is present either way —
 * but on an EARLY cancel it is still in the future, and printing it as the date
 * the trial ended would be false. Only claim a date once it has passed.
 */
export function optidevTrialEndLabelKind(
  trialEndDate: string | undefined,
  now: Date = new Date(),
): 'ended-with-date' | 'ended-no-date' {
  if (!trialEndDate) return 'ended-no-date';
  const end = new Date(trialEndDate);
  if (Number.isNaN(end.getTime())) return 'ended-no-date';
  return end.getTime() <= now.getTime() ? 'ended-with-date' : 'ended-no-date';
}

export function optidevTrialDaysLeft(
  trialEndDate: string | undefined,
  now: Date = new Date(),
): number {
  if (!trialEndDate) return 0;
  const end = new Date(trialEndDate);
  if (Number.isNaN(end.getTime())) return 0;
  return Math.max(Math.ceil((end.getTime() - now.getTime()) / 86_400_000), 0);
}
